import { Box, Grid2 as Grid, Stack, Typography } from "@mui/material"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useParams, useNavigate } from "react-router-dom"
import DialogForm from "../../components/DialogForm"
import { useSnackbarMessage } from "../../hooks/snackbar"
import { DataField, DataFieldLabel, DataFieldLink, DataRow } from "../../components/DataField"
import { SectionTitle } from "../../components/SectionTitle"
import { useTranslation } from "react-i18next"
import RecordInfo from "../../components/RecordInfo"
import DeleteBar from "../../components/DeleteBar"
import DialogConfirm from "../../components/DialogConfirm"
import { Supporto, WithAllegati, WithAllegatiImmagine } from "../../types"
import {
  useSupporto,
  useSupportoDelete,
  useSupportoHistory,
  useSupportoUpdate,
  useSupportoSetAllegati,
  useSupportoSetAllegatiImmagine,
} from "../../hooks/supporti"
import SupportoForm from "../../components/forms/SupportoForm"
import SupportoFormContenuto from "../../components/forms/SupportoFormContenuto"
import { useDesiredHeight } from "../../hooks/measuring"
import AllegatiImmagineForm from "../../components/forms/AllegatiImmagineForm"
import AllegatiForm from "../../components/forms/AllegatiForm"
import AllegatiGrid from "../../components/AllegatiGrid"
import AllegatiImmagineGrid from "../../components/AllegatiImmagineGrid"
import FormatDate from "../../components/FormatDate"
import useUserCanWriteAndDelete from "../../hooks/permissions"
import { useIsMobile } from "../../hooks/mobile"
import { useMemo } from "react"

export default function SupportoDettaglio() {
  const { supportoId: id } = useParams()
  const { t, i18n } = useTranslation()
  const { data: supporto, refetch } = useSupporto(parseInt(id as string))
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()

  const [updateModalState, updateModalActions] = useModalTrigger()
  const [updateContenutoModalState, updateContenutoModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()
  const [updateModalStateAllegati, updateModalActionsAllegati] = useModalTrigger()
  const [updateModalStateAllegatiImmagine, updateModalActionsAllegatiImmagine] = useModalTrigger()

  const { mutateAsync: update } = useSupportoUpdate()
  const { mutateAsync: remove } = useSupportoDelete()
  const { mutateAsync: setAllegati } = useSupportoSetAllegati(id as string)
  const { mutateAsync: setAllegatiImmagine } = useSupportoSetAllegatiImmagine(id as string)

  const showMessage = useSnackbarMessage()
  const navigate = useNavigate()

  const historyBag = useSupportoHistory(id as string)
  const { ref } = useDesiredHeight(24)

  const edgeCodes = useMemo(() => {
    const items = supporto?.edge_codes_data?.map((ec) => {
      return `${ec.sistema_colore_data?.termine || ""} ${ec.cromatismo_data?.termine || ""}: ${ec.edge_code || ""}`.trim()
    })
    return items?.join(", ")
  }, [supporto?.edge_codes_data])

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{
          overflow: "auto",
          height: "100%",
          p: 3,
          pt: 2,
        }}
      >
        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            flexGrow={1}
            sx={{
              height: "fit-content",
              position: "relative",
              bgcolor: "white",
              boxShadow: 1,
            }}
          >
            <SectionTitle
              first
              title={t("sections.identificazione")}
              onEdit={() => {
                updateModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <DataRow>
                <DataField
                  label={t("supporto.fondo")}
                  value={`${supporto?.fondo_data?.denominazione} (${supporto?.fondo_data?.segnatura})`}
                  link={`/fondi/${supporto?.fondo_data?.id}`}
                  size={6}
                />
                <DataField label={t("supporto.segnatura")} value={supporto?.segnatura} size={6} />
              </DataRow>

              <DataRow>
                <DataField label={t("documento.cronologia_da")} value={supporto?.cronologia_da} size={6} />
                <DataField label={t("documento.cronologia_a")} value={supporto?.cronologia_a} size={6} />
              </DataRow>

              <DataRow>
                <DataField label={t("supporto.titolo_proprio")} value={supporto?.titolo_proprio} size={8} />
              </DataRow>

              <DataRow>
                <DataField
                  label={t("fondo.genere")}
                  value={supporto?.genere_data?.map((item) => item.termine)}
                  size={8}
                />
              </DataRow>

              <DataRow>
                <Grid size={12}>
                  <DataFieldLabel label={t("general.documenti")} />
                  <div>
                    {supporto?.documenti_data?.map((doc, index) => (
                      <span key={supporto.id}>
                        <DataFieldLink
                          link={`/documenti/${doc.id}`}
                          key={doc.id}
                          value={doc.titolo_label || t("documento.senza_titolo")}
                        />
                        {index < (supporto.documenti_data || []).length - 1 && ", "}
                      </span>
                    ))}
                  </div>
                </Grid>
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.elemento")} value={supporto?.elemento_data?.termine} size={12} />
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.descr_contenitore")} value={supporto?.descr_contenitore} size={12} />
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.complemento_titolo")} value={supporto?.complemento_titolo} size={6} />
                <DataField label={t("supporto.fonte_titolo")} value={supporto?.fonte_titolo} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.variante")} value={supporto?.variante} size={12} />
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.complemento_variante")} value={supporto?.complemento_variante} size={6} />
                <DataField label={t("supporto.fonte_variante")} value={supporto?.fonte_variante} size={6} />
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.note_titolo")} value={supporto?.note_titolo} size={12} />
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.inv_provvisorio")} value={supporto?.inv_provvisorio} size={12} />
              </DataRow>

              <DataRow last>
                <DataField
                  label={t("supporto.ultimo_assemblaggio")}
                  value={supporto?.assemblaggio_data?.nome}
                  link={`/assemblaggi/${supporto?.assemblaggio_data?.id}`}
                  size={4}
                />
                <DataField
                  label={t("supporto.data_ultimo_assemblaggio")}
                  value={
                    supporto?.data_ultimo_assemblaggio ? (
                      <FormatDate>{supporto.data_ultimo_assemblaggio}</FormatDate>
                    ) : null
                  }
                  size={4}
                />
              </DataRow>
            </Box>

            <SectionTitle
              first
              title={t("sections.inventario_archivistico_contenuto")}
              onEdit={() => {
                updateContenutoModalActions.open()
              }}
            />
            <Box px={3} pb={2}>
              <DataRow>
                <DataField label={t("supporto.formato")} value={supporto?.formato_data?.nome} size={6} />
                <DataField
                  label={t("supporto.lunghezza")}
                  value={`${supporto?.lunghezza} ${supporto?.unit_mis}`}
                  size={6}
                />
              </DataRow>

              <DataRow>
                <DataField label={t("supporto.framerate")} value={supporto?.framerate_data?.termine} size={6} />
                <DataField label={t("supporto.durata_presunta")} value={supporto?.durata_presunta} size={6} />
              </DataRow>

              <DataRow>
                <DataField
                  label={t("supporto.tipologia_materiale")}
                  value={supporto?.tipologia_materiale_data?.termine}
                  size={6}
                />
                <DataField label={t("supporto.tipo_supporto")} value={supporto?.tipo_supporto_data?.termine} size={6} />
              </DataRow>
              <DataRow>
                <DataField
                  label={t("supporto.status_archivio")}
                  value={supporto?.status_archivio_data?.termine}
                  size={6}
                />
              </DataRow>
              <DataRow>
                <DataField label={t("supporto.base_supporto")} value={supporto?.base_supporto_data?.termine} size={6} />

                <DataField label={t("supporto.audio")} value={supporto?.audio_data?.termine} size={6} />
              </DataRow>
              <DataRow>
                <DataField
                  label={t("supporto.tipologia_colonna")}
                  value={supporto?.tipologia_colonna_data?.termine}
                  size={6}
                />
                <DataField
                  label={t("supporto.tipologia_sonoro")}
                  value={supporto?.tipologia_sonoro_data?.termine}
                  size={6}
                />
              </DataRow>

              <DataRow>
                <DataField label={t("supporto.cromatismo")} value={supporto?.cromatismo_data?.termine} size={6} />
                <DataField label={t("supporto.sist_colore")} value={supporto?.sist_colore_data?.termine} size={6} />
              </DataRow>
              <DataRow last>
                <DataField label={t("supporto.edge_codes")} size={12} value={edgeCodes} />
              </DataRow>
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 6,
          }}
        >
          <Stack
            flexGrow={1}
            sx={{
              height: "100%",
              bgcolor: "white",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle first title={t("sections.preview")} />
            <Box px={3} py={2}>
              <DataRow last>
                <Grid size={12}>
                  <DataFieldLabel label={t("supporto.preview")} />
                </Grid>
                <Grid size={12}>
                  {supporto?.preview ? (
                    <video controls src={supporto?.preview} style={{ width: "100%" }} />
                  ) : (
                    <div>
                      <Typography sx={{ display: "flex" }} variant="body1">
                        {t("supporto.nessun_file")}
                      </Typography>
                    </div>
                  )}
                </Grid>
              </DataRow>
            </Box>

            <SectionTitle
              first
              title={t("sections.allegati")}
              onEdit={() => {
                updateModalActionsAllegati.open()
              }}
            />
            <Box px={3} py={2}>
              <AllegatiGrid allegati_data={supporto?.allegati_data || []} />
            </Box>

            <SectionTitle
              first
              title={t("sections.allegati_immagine")}
              onEdit={() => {
                updateModalActionsAllegatiImmagine.open()
              }}
            />
            <Box px={3} py={2}>
              <AllegatiImmagineGrid allegati_data={supporto?.allegati_immagine_data || []} />
            </Box>
          </Stack>
        </Grid>

        <Grid
          size={{
            xs: 12,
            xl: 12,
          }}
        >
          <Stack
            ref={ref}
            sx={{
              bgcolor: "grey.200",
              borderRadius: 1,
              boxShadow: 1,
            }}
          >
            <SectionTitle bgcolor="grey.700" info />
            <Stack sx={{ p: 2 }} flexGrow={1}>
              <RecordInfo
                first_update={supporto?.first_update}
                last_update={supporto?.last_update}
                historyBag={historyBag as any}
              />
            </Stack>
            {userCanWriteAndDelete && !isMobile && supporto?.can_delete && (
              <DeleteBar title={t("supporto.cta_elimina")} onDelete={() => deleteModalActions.open(supporto)} />
            )}
          </Stack>
        </Grid>
      </Grid>

      <DialogForm<Supporto>
        open={updateModalState.isOpen}
        handleClose={updateModalActions.close}
        FormComponent={SupportoForm}
        title={t("supporto.cta_modifica")}
        save={(values) => {
          return update(values).then(() => {
            updateModalActions.close()
            refetch()
            showMessage(t("supporto.esito_modifica_ok"))
          })
        }}
        item={supporto}
        hasCancel
      />

      <DialogForm<Supporto>
        open={updateContenutoModalState.isOpen}
        handleClose={updateContenutoModalActions.close}
        FormComponent={SupportoFormContenuto}
        title={t("supporto.cta_modifica")}
        save={(values) => {
          return update(values).then(() => {
            updateContenutoModalActions.close()
            refetch()
            showMessage(t("supporto.esito_modifica_ok"))
          })
        }}
        item={supporto}
        hasCancel
      />

      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("supporto.cta_elimina")}
        subtitle={
          <span>
            {t("supporto.cta_subtitle_elimina")} <b>"{deleteModalState.value?.segnatura}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() => {
          return remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("supporto.esito_elimina_ok"))
              navigate(`/${i18n.language}/supporti`)
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }}
      />

      <DialogForm<WithAllegati>
        open={updateModalStateAllegati.isOpen}
        handleClose={updateModalActionsAllegati.close}
        FormComponent={AllegatiForm}
        title={t("general.cta_modifica_allegati")}
        save={(values) => {
          const allegatiData = (values.allegati_data || []).map((a) => {
            return {
              id: a.id,
              note: a.note,
              attachment: typeof a.attachment === "string" ? undefined : a.attachment,
            }
          })
          return setAllegati({ allegati: allegatiData }).then(() => {
            updateModalActionsAllegati.close()
            refetch()
            showMessage(t("supporto.esito_modifica_ok"))
          })
        }}
        item={{ allegati_data: supporto?.allegati_data }}
        hasCancel
      />

      <DialogForm<WithAllegatiImmagine>
        open={updateModalStateAllegatiImmagine.isOpen}
        handleClose={updateModalActionsAllegatiImmagine.close}
        FormComponent={AllegatiImmagineForm}
        title={t("general.cta_modifica_allegati_immagine")}
        save={(values) => {
          const allegatiData = (values.allegati_immagine_data || []).map((a) => {
            return {
              id: a.id,
              note: a.note,
              attachment: typeof a.attachment === "string" ? undefined : a.attachment,
            }
          })
          return setAllegatiImmagine({ allegati_immagine: allegatiData }).then(() => {
            updateModalActionsAllegatiImmagine.close()
            refetch()
            showMessage(t("supporto.esito_modifica_ok"))
          })
        }}
        item={{ allegati_immagine_data: supporto?.allegati_immagine_data }}
        hasCancel
      />
    </>
  )
}
